import type { WidgetJSON } from '../../transformers/zod-schemas';
import type { ValidationStatus } from '../widgets/types';
import type { LineData } from '../widgets/pair-matcher/usePairLines';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PairState = {
  pairs: Record<string, string>;
  lineDirections: Record<string, 'top-to-bottom' | 'bottom-to-top'>;
  pending: PendingSelection;
  /** Recently paired cards get a short selected-card flash while the line appears. */
  flashTopId: string | null;
  flashBottomId: string | null;
};

export type PendingSelection = { side: 'top' | 'bottom'; id: string } | null;
export type PairStatus = 'idle' | 'pending' | 'paired' | 'correct' | 'incorrect';

// ─── Stable module-level constants ───────────────────────────────────────────

export const EMPTY_WIDGETS: Record<string, WidgetJSON> = {};
export const EMPTY_VALUES: Record<string, never> = {};
export const EMPTY_DROPPED = new Set<string>();
export const NOOP = () => {};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

export function buildInitialState(
  defaultValue: string[][] | undefined,
  topIds: Set<string>,
  bottomIds: Set<string>,
): { pairs: Record<string, string>; lineDirections: Record<string, 'top-to-bottom' | 'bottom-to-top'> } {
  if (!defaultValue?.length) return { pairs: {}, lineDirections: {} };
  const pairs: Record<string, string> = {};
  const lineDirections: Record<string, 'top-to-bottom' | 'bottom-to-top'> = {};
  for (const pair of defaultValue) {
    if (!Array.isArray(pair) || pair.length !== 2 || pair[0] == null || pair[1] == null) continue;
    const [first, second] = pair as [string, string];
    if (topIds.has(first) && bottomIds.has(second)) {
      pairs[first] = second;
      lineDirections[first] = 'top-to-bottom';
    } else if (bottomIds.has(first) && topIds.has(second)) {
      pairs[second] = first;
      lineDirections[second] = 'bottom-to-top';
    }
  }
  return { pairs, lineDirections };
}

export function findTopForBottom(pairs: Record<string, string>, bottomId: string): string | undefined {
  return Object.keys(pairs).find(topId => pairs[topId] === bottomId);
}

export function withLineDirection(
  lineDirections: Record<string, 'top-to-bottom' | 'bottom-to-top'>,
  pairs: Record<string, string>,
  topId: string,
  bottomId: string,
  direction: 'top-to-bottom' | 'bottom-to-top',
): Record<string, 'top-to-bottom' | 'bottom-to-top'> {
  const next = { ...lineDirections };
  delete next[topId];
  const existingTop = findTopForBottom(pairs, bottomId);
  if (existingTop !== undefined) delete next[existingTop];
  next[topId] = direction;
  return next;
}

export function getPairIds(
  pair: string[],
  topIds: Set<string>,
  bottomIds: Set<string>,
): { topId: string; bottomId: string } | null {
  if (pair.length !== 2) return null;
  const first = pair[0];
  const second = pair[1];
  if (first === undefined || second === undefined) return null;
  if (topIds.has(first) && bottomIds.has(second)) return { topId: first, bottomId: second };
  if (bottomIds.has(first) && topIds.has(second)) return { topId: second, bottomId: first };
  return null;
}

export function withPair(
  pairs: Record<string, string>,
  topId: string,
  bottomId: string,
): Record<string, string> {
  const next = { ...pairs };
  delete next[topId];
  const existingTop = findTopForBottom(next, bottomId);
  if (existingTop !== undefined) delete next[existingTop];
  next[topId] = bottomId;
  return next;
}

/** Compute the visual status of a card given current interaction + validation state. */
export function resolveCardStatus(
  ownId: string,
  isTop: boolean,
  pairs: Record<string, string>,
  pending: PendingSelection,
  validationStatus: ValidationStatus | null | undefined,
  pairStatus: 'correct' | 'incorrect' | null,
): PairStatus {
  // ── Validation mode ──
  if (validationStatus) {
    const topId = isTop ? ownId : findTopForBottom(pairs, ownId);
    if (topId === undefined || pairs[topId] === undefined) return 'idle';
    return pairStatus ?? (validationStatus === 'correct' ? 'correct' : 'incorrect');
  }

  // ── Interaction mode ──
  if (pending?.side === (isTop ? 'top' : 'bottom') && pending.id === ownId) return 'pending';
  const topId = isTop ? ownId : findTopForBottom(pairs, ownId);
  if (topId !== undefined && pairs[topId] !== undefined) return 'paired';
  return 'idle';
}

export function buildDotClass(status: PairStatus, position: 'south' | 'north'): string {
  const cls = ['pair-matcher-dot', `pair-matcher-dot--${position}`];
  if (status !== 'idle') {
    cls.push('pair-matcher-dot--active');
    if (status !== 'paired') cls.push(`pair-matcher-dot--${status}`);
  }
  return cls.join(' ');
}

export function buildCardClass(status: PairStatus, isFlashing: boolean): string {
  const cls = ['pair-matcher-card'];
  if (status !== 'idle') cls.push(`pair-matcher-card--${status}`);
  if (isFlashing) cls.push('pair-matcher-card--flash');
  return cls.join(' ');
}

export function lineStrokeVar(status: LineData['status']): string {
  if (status === 'correct')   return 'var(--pm-line-color-correct)';
  if (status === 'incorrect') return 'var(--pm-line-color-incorrect)';
  return 'var(--pm-line-color)';
}
